import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
import { logEvent } from '../../../../services/analytics/eventLogger';

// Just long enough for the page to land as theirs first. 2600 → 1800 → 900
// across two device passes: past about a second the pause stops reading as a
// beat and starts reading as something sneaking in (user, 2026-08-16).
const REVEAL_MS = 900;

// Marks the FIRST arrival, which is the only one that animates. It does not
// suppress the card afterwards — see the permanence note below.
const SEEN_KEY = 'anon_people_reassurance_seen';

/**
 * PeopleQrPitch — the pitch under the People list, above the Connect button
 * (user design 2026-08-16). Used on BOTH People pages, now in two SHAPES:
 *
 *   Simple (has a QR)     — `variant="card"`, the default: the white card with
 *                           the headline and benefit lines, minus the QR
 *                           invitation (`showQrLine={false}`), which is stale
 *                           the moment they own one.
 *   Anon (no QR yet)      — `variant="line"`: the QR invitation ALONE, no card,
 *                           no headline, no benefit lines.
 *
 * WHY THE ANON SIDE LOST THE CARD (user, 2026-08-20): "the current pitch and
 * explainer is annoying — the app should speak for itself." The card was
 * written when the Anon page's job was to sell the QR. Its job now is to sell
 * the APP (the Get-Royale banner below the button), and a three-line pitch for
 * the QR sitting above a banner for the app is two pitches competing on one
 * screen. The QR invitation survives as a single quiet line because we still
 * want the mint — we just no longer want to argue for it.
 *
 * The reveal machinery below is deliberately SHARED by both shapes rather than
 * re-hosted for the line: it owns the once-ever flag, the delayed entrance and
 * the `anon_people_reassurance_shown` event, and that event is a live
 * hypothesis measure whose continuity is worth more than a tidier split.
 *
 * It RETIRES ON COMPLETION, not on exposure (user idea 2026-08-16): the SP
 * keeps the two-line reminder until their own QR actually produces a
 * connection — funnel step 3, the milestone's hero metric. "Seen once" retires
 * on having looked; this retires when the thing it is asking for happens, and
 * the window it covers is exactly the step-2 → step-3 gap.
 *
 * IT ARRIVES ONCE, THEN IT STAYS. The first visit gets the delayed entrance;
 * every visit after renders it immediately and statically, as page furniture.
 * That is deliberate and it fixes two problems at once (user ruling
 * 2026-08-16):
 *
 *   - A once-ever message shows the explanation to the person BROWSING and
 *     withholds it from the person DECIDING — and the decider is the returner,
 *     who comes back a day later to an unexplained black button.
 *   - Furniture cannot nag. The 2026-07-17 freeze ("a real tester read the big
 *     unprompted CTA as a demand") was about something that interrupted; this
 *     interrupts nothing, has no scrim and no dismiss, and the Connect button
 *     stays live underneath the whole time.
 *
 * THE COPY IS A PERSUASION RAMP, not a description (user ruling 2026-08-16,
 * revising the earlier page-describing headline — "Everyone you connect with"
 * was friendly and had no action in it):
 *
 *   "Connect more IRL"              — the GOAL in the person's own terms, as an
 *                                     imperative. It echoes the button under it
 *                                     on purpose: headline states the aim, the
 *                                     button is the verb. Beat two candidates —
 *                                     "Be ready next time" (a nudge about
 *                                     future utility, softer) and "Be a
 *                                     connector" (identity is a bigger ask than
 *                                     an action, and "connector" carries a
 *                                     networking-guy flavour that cuts against
 *                                     the warm in-person positioning).
 *   "Remember where you met"        — the benefit, and the one the connection
 *                                     card directly above it is DEMONSTRATING
 *                                     ("Sorso, Just now / New York, NY"). Beat
 *                                     "Private chats", a bare noun phrase with
 *                                     no verb and no benefit.
 *   "Share only what you want"        — objection 1, what will this cost me?
 *                                     This is also where privacy lives now, in
 *                                     its specific form. Never escalate to
 *                                     "secure" or "encrypted" — we can't back
 *                                     those.
 *   "Try a QR without an account"   — objection 2, and the strongest line,
 *                                     because signup is what they are bracing
 *                                     for and the answer is no. "Try" over
 *                                     "make": lowest-commitment verb available,
 *                                     which is this line's entire job.
 */
const PeopleQrPitch = ({ showQrLine = true, variant = 'card' }) => {
  // Read once at mount: a returner should never see the card pop in late.
  const [seenBefore] = useState(() => localStorage.getItem(SEEN_KEY) === 'true');
  // Derived from the one read above — two initializers reading the same key
  // were two chances for the states to disagree.
  const [show, setShow] = useState(seenBefore);

  useEffect(() => {
    if (seenBefore) return undefined;
    const t = setTimeout(() => {
      localStorage.setItem(SEEN_KEY, 'true');
      setShow(true);
      // The whole point of this card is a hypothesis — that naming what we
      // don't ask for converts the suspicious. Without an event we would ship
      // it and learn only the mint rate, never whether this moved it.
      logEvent('anon_people_reassurance_shown');
    }, REVEAL_MS);
    return () => clearTimeout(t);
  }, [seenBefore]);

  if (!show) return null;

  // The bare invitation — same copy, same colour, no container. "Try" over
  // "make" still: lowest-commitment verb available, which is this line's whole
  // job, and the only job left on this page now that the app is the main path.
  if (variant === 'line') {
    return (
      <motion.div
        initial={seenBefore ? false : { opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: [0.2, 0.8, 0.2, 1] }}
        style={{
          width: '100%',
          textAlign: 'center',
          fontSize: '16px',
          color: '#8E8E93',
        }}
      >
        Try the QR without an account
      </motion.div>
    );
  }

  return (
    <motion.div
      // Rises from BEHIND the Connect button (user 2026-08-16): it starts far
      // enough down that its lower edge is hidden by the button, so the card
      // emerges from the control rather than fading in above it. The button's
      // wrapper carries the higher z-index — see AnonConnectionsListPage.
      initial={seenBefore ? false : { opacity: 0, y: 56 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, ease: [0.2, 0.8, 0.2, 1] }}
      style={{
        position: 'relative',
        zIndex: 1,
        width: '100%',
        maxWidth: '320px',
        margin: '0 auto',
        padding: '18px 20px',
        boxSizing: 'border-box',
        backgroundColor: '#FFFFFF',
        border: '1px solid #F0F0F2',
        borderRadius: '18px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
        textAlign: 'center',
      }}
    >
      <div style={{ fontSize: '17px', fontWeight: '700', color: '#000000' }}>Connect more IRL</div>
      {/* Four SHORT lines beat three with one wrapping (user, device
          2026-08-16 — "Private chats, share only what you want" broke after
          "you" and left "want" stranded). */}
      <div style={{ fontSize: '16px', color: '#3A3A3C', marginTop: '10px' }}>
        Remember where you met
      </div>
      <div style={{ fontSize: '16px', color: '#3A3A3C', marginTop: '6px' }}>
        Share only what you want
      </div>
      {/* The invitation lands a beat AFTER the two lines above it, with more
          air — motion is what makes it the noticed line, the same trick the
          count badge uses. Only on the first arrival; a returner gets the whole
          card at once. Deliberately NOT italic: italics read as quotation
          rather than invitation and are harder to read at this size on iOS. */}
      {showQrLine && (
        <motion.div
          initial={seenBefore ? false : { opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: 'easeOut', delay: seenBefore ? 0 : 0.55 }}
          style={{ fontSize: '16px', color: '#8E8E93', marginTop: '16px' }}
        >
          Try the QR without an account
        </motion.div>
      )}
    </motion.div>
  );
};

PeopleQrPitch.propTypes = {
  showQrLine: PropTypes.bool,
  /** 'card' = the white pitch card (Simple tier); 'line' = the QR invitation alone (Anon) */
  variant: PropTypes.oneOf(['card', 'line']),
};

export default PeopleQrPitch;
